import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Polygon, Polyline, CircleMarker, Tooltip, useMap } from "react-leaflet";
import LeafletMap, { FitBounds, ZONE_PALETTE } from "@/components/map/LeafletMap";
import OsmAmenityLayers from "@/components/map/OsmAmenityLayers";
import { AmenityToggles } from "@/components/map/AmenityToggles";
import { api } from "@/lib/api";
import BikePathCard from "@/components/rider/app/BikePathCard";
import { Bike, Search, Loader2, MapPin, Compass, Store, Megaphone, Navigation, LocateFixed, SlidersHorizontal, Check, X, Home } from "lucide-react";
import { toast } from "sonner";

const isLL = (c) => Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1]);
const validLatLngs = (arr) => (arr || []).filter(isLL);

// Harvested route geometry: prefer the flat centerline, else flatten the
// multi-segment `lines`. Already [lat,lng] — no flip needed.
const routePositions = (p) => {
  const raw = p.centerline && p.centerline.length ? p.centerline : (p.lines || []).flat();
  return validLatLngs(raw);
};

const FILTER_GROUPS = [
  { group: "surfaces", label: "Surface", opts: [["paved", "Paved"], ["crushed_stone", "Crushed Stone"], ["gravel", "Gravel"], ["dirt", "Dirt"]] },
  { group: "difficulty", label: "Difficulty", opts: [["easy", "Easy"], ["moderate", "Moderate"], ["hard", "Hard"]] },
  { group: "length", label: "Trail Length", opts: [["short", "Short (<5 mi)"], ["medium", "Medium (5–15 mi)"], ["long", "Long (>15 mi)"]] },
  { group: "amenities", label: "Amenities", opts: [["parking", "Parking"], ["bathrooms", "Bathrooms"], ["water", "Water"], ["bike_shops", "Bike Shops"], ["restaurants", "Restaurants"]] },
  { group: "suitability", label: "Rider Type", opts: [["family_friendly", "Family Friendly"], ["beginner_friendly", "Beginner Friendly"], ["ebike_friendly", "E-Bike Friendly"], ["dog_friendly", "Dog Friendly"]] },
];

function centerOf(p) {
  if (isLL(p.center)) return p.center;
  const b = validLatLngs(p.boundary);
  if (b.length) {
    const lat = b.reduce((s, x) => s + x[0], 0) / b.length;
    const lng = b.reduce((s, x) => s + x[1], 0) / b.length;
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }
  return null;
}

// Imperatively flies the map to a target path when `nonce` changes.
function FlyToBounds({ positions, nonce }) {
  const map = useMap();
  useEffect(() => {
    try {
      const pts = (positions || []).filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
      if (pts.length === 0) return;
      if (pts.length === 1) map.flyTo(pts[0], 14, { duration: 0.8 });
      else map.flyToBounds(pts, { padding: [50, 50], maxZoom: 15, duration: 0.8 });
    } catch {
      /* never blank the page over a map animation */
    }
  }, [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function ExplorePage() {
  const nav = useNavigate();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [hoverId, setHoverId] = useState(null);
  const [focus, setFocus] = useState(null);
  const [locating, setLocating] = useState(false);
  const [filters, setFilters] = useState({ surfaces: [], difficulty: [], length: [], amenities: [], suitability: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [amenVisible, setAmenVisible] = useState({});
  const toggleAmen = (k) => setAmenVisible((s) => ({ ...s, [k]: !s[k] }));
  const activeCount = Object.values(filters).reduce((n, a) => n + a.length, 0);
  const toggleFilter = (group, val) => setFilters((f) => ({ ...f, [group]: f[group].includes(val) ? f[group].filter((x) => x !== val) : [...f[group], val] }));
  const clearFilters = () => setFilters({ surfaces: [], difficulty: [], length: [], amenities: [], suitability: [] });
  const inLength = (mi, b) => mi != null && ((b === "short" && mi < 5) || (b === "medium" && mi >= 5 && mi <= 15) || (b === "long" && mi > 15));
  const matchesFilters = (p) => {
    const f = filters;
    if (f.surfaces.length && !(p.surfaces || []).some((s) => f.surfaces.includes(s))) return false;
    if (f.difficulty.length && !f.difficulty.some((d) => (p.difficulty || "").toLowerCase().includes(d))) return false;
    if (f.length.length && !f.length.some((b) => inLength(p.length_mi, b))) return false;
    if (f.amenities.length && !f.amenities.every((a) => p[`has_${a}`])) return false;
    if (f.suitability.length && !f.suitability.every((s) => p[s])) return false;
    return true;
  };

  useEffect(() => {
    api.get("/rider/home", { params: { limit: 0 } })
      .then(({ data }) => setPaths(data.bike_paths || []))
      .catch(() => setPaths([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return paths.filter((p) => {
      if (ql && !(p.name || "").toLowerCase().includes(ql)) return false;
      return matchesFilters(p);
    });
  }, [paths, q, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const bounds = useMemo(() => {
    const pts = [];
    filtered.forEach((p) => {
      const route = routePositions(p);
      if (route.length >= 2) route.forEach((c) => pts.push(c));
      const vb = validLatLngs(p.boundary);
      if (vb.length >= 2) vb.forEach((c) => pts.push(c));
      else if (route.length < 2) { const c = centerOf(p); if (c) pts.push(c); }
    });
    return pts.length >= 2 ? pts : null;
  }, [filtered]);

  const amenCounts = useMemo(() => {
    const c = { osm_park: 0, osm_bathroom: 0, osm_parking: 0 };
    filtered.forEach((p) => { const a = p.osm_amenities || {}; c.osm_park += (a.parks || []).length; c.osm_bathroom += (a.bathrooms || []).length; c.osm_parking += (a.parking || []).length; });
    return c;
  }, [filtered]);
  const anyAmen = amenCounts.osm_park + amenCounts.osm_bathroom + amenCounts.osm_parking > 0;

  const goDetail = (id) => nav(`/rider/bike-path/${id}`);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't supported on this device"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const { data } = await api.get(`/rider/home?lat=${latitude}&lng=${longitude}&limit=1`);
          const closest = data.closest_bike_path;
          const c = closest ? centerOf(closest) : null;
          const vb = closest ? validLatLngs(closest.boundary) : [];
          const positions = vb.length >= 2 ? vb : (c ? [c] : null);
          if (!positions) { toast.info("No bike paths found near you"); return; }
          setQ("");
          if (Array.isArray(data.bike_paths) && data.bike_paths.length) setPaths(data.bike_paths); // distance-ordered
          setHoverId(closest.id);
          setFocus({ positions, nonce: Date.now() });
          toast.success(`Nearest path: ${closest.name}`);
        } catch {
          toast.error("Couldn't find paths near you");
        } finally {
          setLocating(false);
        }
      },
      () => { setLocating(false); toast.info("Location permission denied"); }, // denied -> map unchanged
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const mapEl = (height) => (
    <div className="relative" data-testid="explore-map-wrap">
      {anyAmen && (
        <div className="absolute top-2 left-14 z-[1000] max-w-[calc(100%-4rem)] bg-background/90 backdrop-blur rounded-lg p-1.5 border border-border">
          <AmenityToggles visible={amenVisible} onToggle={toggleAmen} available={amenCounts} />
        </div>
      )}
      <LeafletMap height={height} center={bounds?.[0] || [41.75, -71.3]} zoom={9}>
      {bounds && <FitBounds bounds={bounds} nonce={filtered.length} />}
      {focus && <FlyToBounds positions={focus.positions} nonce={focus.nonce} />}
      {filtered.map((p, i) => {
        const color = ZONE_PALETTE[i % ZONE_PALETTE.length];
        const c = centerOf(p);
        const active = hoverId === p.id;
        return (
          <Fragment key={p.id}>
            {(() => {
              const route = routePositions(p);
              if (route.length < 2) return null;
              return (
                <>
                  <Polyline positions={route} pathOptions={{ color: "#ffffff", weight: 6, opacity: 0.9 }} interactive={false} />
                  <Polyline positions={route} pathOptions={{ color: "#0ea5e9", weight: 4, opacity: 1 }}
                    eventHandlers={{ click: () => goDetail(p.id), mouseover: () => setHoverId(p.id), mouseout: () => setHoverId(null) }}>
                    <Tooltip>{p.name}</Tooltip>
                  </Polyline>
                </>
              );
            })()}
            {validLatLngs(p.boundary).length >= 3 && (
              <Polygon positions={validLatLngs(p.boundary)}
                pathOptions={{ color, fillColor: color, weight: active ? 3 : 2, fillOpacity: active ? 0.3 : 0.12 }}
                eventHandlers={{ click: () => goDetail(p.id), mouseover: () => setHoverId(p.id), mouseout: () => setHoverId(null) }}>
                <Tooltip>{p.name}</Tooltip>
              </Polygon>
            )}
            {c && (
              <CircleMarker center={c} radius={active ? 11 : 8}
                pathOptions={{ color: "#fff", weight: 2, fillColor: color, fillOpacity: 1 }}
                eventHandlers={{ click: () => goDetail(p.id), mouseover: () => setHoverId(p.id), mouseout: () => setHoverId(null) }}>
                <Tooltip>{p.name}</Tooltip>
              </CircleMarker>
            )}
          </Fragment>
        );
      })}
      {filtered.map((p) => <OsmAmenityLayers key={`am-${p.id}`} data={p.osm_amenities || {}} visible={amenVisible} />)}
    </LeafletMap>
    </div>
  );

  const listEl = (
    <div className="space-y-3" data-testid="explore-path-list">
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground" data-testid="explore-empty">{activeCount > 0 ? "No bike paths match your filters." : `No bike paths match "${q}".`}</div>
      ) : (
        filtered.map((p) => (
          <div key={p.id} onMouseEnter={() => setHoverId(p.id)} onMouseLeave={() => setHoverId(null)}
            className={`rounded-2xl transition-all ${hoverId === p.id ? "ring-2 ring-primary" : ""}`} data-testid={`explore-card-wrap-${p.id}`}>
            <BikePathCard p={p} onClick={() => goDetail(p.id)} />
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-950 text-foreground" data-testid="explore-page">
      {/* Header */}
      <header className="sticky top-0 z-40 flex items-center gap-3 px-4 lg:px-8 h-16 bg-background/90 backdrop-blur border-b border-border">
        <a href="https://velopaths.com" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="nav-logo-home">
          <Bike className="w-6 h-6 text-primary" />
          <span className="font-heading font-bold tracking-tight text-lg">VeloPaths</span>
          <span className="hidden sm:inline text-[11px] uppercase tracking-wider text-muted-foreground font-semibold ml-1">Explore</span>
        </a>
        <nav className="ml-auto flex items-center gap-1.5 sm:gap-3">
          <a href="https://velopaths.com" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors" data-testid="nav-home-link"><Home className="w-4 h-4" />Home</a>
          <button onClick={() => nav("/signup/advertiser")} className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors" data-testid="explore-nav-advertiser"><Megaphone className="w-4 h-4" />List a business</button>
          <button onClick={() => nav("/signup/shop")} className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors" data-testid="explore-nav-shop"><Store className="w-4 h-4" />Add your shop</button>
          <button onClick={() => nav("/login")} className="inline-flex items-center rounded-full bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 hover:opacity-90 active:scale-95 transition-all" data-testid="explore-nav-login">Partner login</button>
        </nav>
      </header>

      {/* Desktop: map left (sticky) + list right. Mobile: map on top, list below. */}
      <div className="lg:flex lg:items-start">
        <div className="lg:w-[65%] xl:w-[68%] lg:sticky lg:top-16" data-testid="explore-map">
          <div className="lg:hidden">{mapEl("42vh")}</div>
          <div className="hidden lg:block">{mapEl("calc(100vh - 4rem)")}</div>
        </div>

        <aside className="lg:w-[35%] xl:w-[32%] lg:h-[calc(100vh-4rem)] lg:overflow-y-auto px-4 lg:px-6 py-6" data-testid="explore-panel">
          <div className="inline-flex items-center gap-1.5 text-primary text-xs font-bold uppercase tracking-wider"><Compass className="w-4 h-4" />Explore VeloPaths</div>
          <h1 className="font-heading font-extrabold text-2xl lg:text-3xl leading-tight mt-2">Find a bike path near you</h1>
          <p className="text-sm text-muted-foreground mt-2">Browse trails, discover restaurants, attractions and bike shops along the way — right from your browser, no app required.</p>

          <div className="relative mt-5">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bike paths…" data-testid="explore-search"
              className="w-full rounded-full bg-card border border-border pl-9 pr-4 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
          </div>

          <button onClick={useMyLocation} disabled={locating} data-testid="explore-use-location"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-card border border-border py-2.5 text-sm font-semibold hover:border-primary/60 hover:text-primary active:scale-[0.99] transition-all disabled:opacity-60">
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
            {locating ? "Locating…" : "Use my location"}
          </button>

          <div className="mt-3">
            <button onClick={() => setShowFilters((s) => !s)} data-testid="explore-filters-toggle"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-card border border-border py-2.5 text-sm font-semibold hover:border-primary/60 hover:text-primary active:scale-[0.99] transition-all">
              <SlidersHorizontal className="w-4 h-4" />Filters{activeCount > 0 ? ` (${activeCount})` : ""}
            </button>
            {showFilters && (
              <div className="mt-3 rounded-2xl bg-card border border-border p-4 space-y-4" data-testid="explore-filters-panel">
                {FILTER_GROUPS.map((g) => (
                  <div key={g.group} data-testid={`explore-filter-group-${g.group}`}>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2">{g.label}</div>
                    <div className="flex flex-wrap gap-2">
                      {g.opts.map(([val, lbl]) => {
                        const on = filters[g.group].includes(val);
                        return (
                          <button key={val} onClick={() => toggleFilter(g.group, val)} data-testid={`explore-filter-${g.group}-${val}`}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/50"}`}>
                            {on && <Check className="w-3 h-3" />}{lbl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {activeCount > 0 && (
                  <button onClick={clearFilters} data-testid="explore-filters-clear"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors">
                    <X className="w-3.5 h-3.5" />Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
            <MapPin className="w-3.5 h-3.5 text-primary" />
            <span data-testid="explore-count">{loading ? "Loading paths…" : `${filtered.length} bike path${filtered.length === 1 ? "" : "s"}`}</span>
          </div>

          <div className="mt-4">{listEl}</div>
        </aside>
      </div>
    </div>
  );
}
